import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { apiService } from '../services/api';
import type { Work } from '../types/api';
import { Button, Carousel, CtaLink, Eyebrow, Lightbox, ProjectCard, Reveal, RichText, RichHtml, Section } from '../components/ui';
import { siteInfo } from '../components/layout/siteInfo';
import { capitalize, formatLocation } from '../utils/text';
import { deliveryLabel, longDate, parseWorkText, workStatus } from '../utils/work';

const bad = (u?: string) => !u || /placeholder\.com/i.test(u);

export default function WorkDetail() {
  const { id } = useParams<{ id: string }>();
  const [work, setWork] = useState<Work | null>(null);
  const [related, setRelated] = useState<Work[]>([]);
  const [status, setStatus] = useState<'loading' | 'ok' | 'error'>('loading');
  const [seen, setSeen] = useState(false);
  const [lightbox, setLightbox] = useState<number | null>(null);

  useEffect(() => {
    if (!id) { setStatus('error'); return; }
    let alive = true;
    setStatus('loading'); setSeen(false); setRelated([]);
    apiService.getWork(id)
      .then((w) => {
        if (!alive) return;
        setWork(w); setStatus('ok');
        requestAnimationFrame(() => alive && setSeen(true));
        apiService.getWorks({ enable: true }).then((all) => {
          if (!alive) return;
          const others = all.filter((x) => x.id !== w.id && x.cover_image?.url);
          const same = others.filter((x) => x.service_id === w.service_id);
          setRelated([...same, ...others.filter((x) => x.service_id !== w.service_id)].slice(0, 6));
        }).catch(() => {});
      })
      .catch(() => alive && setStatus('error'));
    return () => { alive = false; };
  }, [id]);

  const text = useMemo(() => parseWorkText(work?.subtitle), [work?.subtitle]);
  const gallery = useMemo(() => (work?.work_gallery ?? []).map((g) => g.gallery).filter((g): g is NonNullable<typeof g> => !!g && !bad(g.url)), [work]);
  // Description détaillée saisie dans l'admin (texte riche); à défaut, texte brut découpé en blocs
  const detailBlocks = useMemo(() => (work?.contentHtml ? [] : parseWorkText(work?.content).blocks), [work?.content, work?.contentHtml]);
  const viewer = useMemo(() => gallery.map((g) => ({ src: g.url, alt: g.legende || work?.title || '' })), [gallery, work?.title]);

  if (status === 'loading') {
    return <div aria-busy className="animate-pulse"><div className="h-[22rem] bg-secondary-200 md:h-[42rem]" /><div className="site-container mt-12 space-y-4"><div className="h-4 w-40 bg-secondary-200" /><div className="h-16 w-3/4 bg-secondary-200" /></div></div>;
  }
  if (status === 'error' || !work) {
    return (
      <Section eyebrow="Réalisations" heading="Projet introuvable" background="white">
        <p className="mb-8 max-w-xl text-lg text-ink">Ce projet n'existe pas ou n'est plus disponible.</p>
        <CtaLink to="/portfolio">Retour aux réalisations</CtaLink>
      </Section>
    );
  }

  const st = workStatus(work);
  const mode = deliveryLabel(work.delivery_method);
  const location = formatLocation(work.location);
  const facts: { label: string; value: string }[] = [
    { label: 'Client', value: work.client?.trim() ?? '' },
    { label: "Mode d'intervention", value: mode },
    { label: 'Lieu', value: location },
    { label: 'Service', value: work.service?.name?.trim() ?? '' },
    { label: 'Début', value: longDate(work.start_date) },
    { label: Date.parse(work.completed_date ?? '') > Date.now() ? 'Livraison prévue' : 'Date de fin', value: longDate(work.completed_date) },
    ...text.facts,
  ].filter((f) => f.value);

  const aside = gallery[0];

  return (
    <article data-seen={seen} className="bg-white text-black">
      {/* Photo pleine largeur, avec un aplat de marque en biais dans le coin bas droit */}
      <div className="relative h-[22rem] overflow-hidden bg-charcoal md:h-[42rem]">
        {work.cover_image?.url && <img src={work.cover_image.url} alt={work.title} className="absolute inset-0 h-full w-full animate-fade-in object-cover" />}
        <svg aria-hidden viewBox="0 0 1440 672" preserveAspectRatio="xMaxYMax slice" className="pointer-events-none absolute inset-0 hidden h-full w-full lg:block">
          <polygon className="ih-tri" points="1230,672 1440,380 1440,672" fill="var(--color-brand)" fillOpacity="0.55" />
        </svg>
      </div>

      {/* Pas de balise <header>: l'ancien CSS la force en position fixe */}
      <div className="site-container pb-10 pt-12 md:pb-14 md:pt-16">
        <Eyebrow>Détail du projet</Eyebrow>
        <h1 className="mt-5 max-w-5xl font-display text-[2.25rem] font-bold normal-case leading-[1.05] tracking-normal text-black md:text-[3.5rem]" style={{ display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{capitalize(work.title)}</h1>
        <div className="mt-6 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs font-semibold uppercase tracking-[0.0625rem]">
          <span className="inline-flex items-center gap-2"><span aria-hidden className={`h-2.5 w-2.5 rounded-full ${st.label === 'Terminé' ? 'bg-steel' : 'bg-brand'}`} />{st.label}</span>
          <span className="font-normal normal-case tracking-normal text-ink">{st.detail}</span>
        </div>
      </div>

      {/* Fiche du projet */}
      {facts.length > 0 && (
        <div className="site-container">
          <dl className="grid gap-x-10 gap-y-7 border-b border-line pb-10 sm:grid-cols-2 lg:grid-cols-3">
            {facts.map((f) => (
              <div key={f.label}>
                <dt className="text-xs font-medium uppercase tracking-[0.0625rem] text-ink">{f.label}</dt>
                <dd className="mt-1.5 text-[0.9375rem] font-bold uppercase tracking-[0.03em]">{f.value}</dd>
              </div>
            ))}
          </dl>
        </div>
      )}

      {/* Texte à gauche; à droite, photo, statut et carte « Réalisé par » restent visibles pendant la lecture */}
      <div className="site-container grid gap-14 py-14 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] lg:gap-x-28 lg:py-20">
        <div>
          {text.blocks[0]?.kind === 'p' && (
            <Reveal><h2 className="mb-6 font-display text-[1.75rem] font-bold normal-case leading-tight tracking-normal text-black md:text-[2.25rem]">À propos de ce projet</h2></Reveal>
          )}
          {text.blocks.length > 0 ? <RichText blocks={text.blocks} firstHeadingLarge /> : <p className="text-ink">La description détaillée de ce projet sera bientôt disponible.</p>}
        </div>

        <aside className="space-y-8 lg:sticky lg:top-28 lg:self-start">
          {aside && (
            <Reveal>
              <figure>
                <button onClick={() => setLightbox(0)} aria-label="Agrandir la photo" className="group relative block w-full cursor-zoom-in overflow-hidden">
                  <img src={aside.url} alt={aside.legende || work.title} loading="lazy" className="aspect-[4/3] w-full object-cover transition-transform duration-700 group-hover:scale-[1.03]" />
                </button>
                {aside.legende && <figcaption className="mt-3 text-sm text-ink">{aside.legende}</figcaption>}
              </figure>
            </Reveal>
          )}

          <Reveal delay={0.08}>
            <div className="bg-surface p-8">
              <div className="text-xs font-semibold uppercase tracking-[0.0625rem] text-ink">Réalisé par</div>
              <div className="mt-4 flex flex-col items-start gap-4 sm:flex-row sm:gap-5">
                <img src="/logo.png" alt="Flex Group" className="h-14 w-auto shrink-0" />
                <div className="text-[0.9375rem] leading-relaxed">
                  <div className="font-display text-lg font-bold leading-tight">{siteInfo.name}</div>
                  <p className="mt-1 text-ink">{siteInfo.address}</p>
                  <a href={siteInfo.phoneHref} className="mt-2 block font-medium hover:text-brand-dark">{siteInfo.phone}</a>
                </div>
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.12}>
            <div className="bg-charcoal p-8 text-white md:p-10">
              <div className="text-xs font-semibold uppercase tracking-[0.0625rem] text-brand">Un projet similaire ?</div>
              <div className="mt-3 font-display text-[1.75rem] font-bold leading-tight text-white">Parlons de votre chantier</div>
              <p className="mt-2 text-[0.9375rem] !text-white/80">De l'étude à la livraison, notre équipe vous accompagne.</p>
              <div className="mt-6"><Button to="/contact" variant="secondary-light" className="w-full">Démarrer un projet</Button></div>
            </div>
          </Reveal>
        </aside>
      </div>

      {/* Description détaillée du projet, rédigée dans l'admin */}
      {(work.contentHtml || detailBlocks.length > 0) && (
        <section className="bg-surface py-16 md:py-24">
          <div className="site-container">
            <Eyebrow className="mb-4">Le projet en détail</Eyebrow>
            <div className="max-w-3xl">
              {work.contentHtml ? <RichHtml html={work.contentHtml} /> : <RichText blocks={detailBlocks} />}
            </div>
          </div>
        </section>
      )}

      {/* Galerie: les photos suivantes, cliquables pour agrandir */}
      {gallery.length > 1 && (
        <section className="py-16 md:py-24">
          <div className="site-container">
            <Eyebrow className="mb-4">Galerie</Eyebrow>
            <h2 className="mb-10 font-display text-[2rem] font-bold normal-case leading-[1.1] tracking-normal text-black md:text-[2.75rem]">Le chantier en images</h2>
            <ul className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {gallery.slice(1).map((g, n) => (
                <li key={g.id}>
                  <button onClick={() => setLightbox(n + 1)} aria-label={`Agrandir la photo ${n + 2}`} className="group relative block aspect-[4/3] w-full cursor-zoom-in overflow-hidden bg-secondary-200">
                    <img src={g.url} alt={g.legende || work.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                    <span aria-hidden className="absolute inset-x-0 bottom-0 h-1 origin-left scale-x-0 bg-brand transition-transform duration-500 group-hover:scale-x-100" />
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}

      {/* Autres réalisations: même service d'abord */}
      {related.length > 0 && (
        <section className="bg-white py-16 pb-20 md:py-24">
          <div className="site-container">
            <Carousel variant="projects" eyebrow="À découvrir" title="Autres réalisations">
              {related.map((r) => (
                <ProjectCard key={r.id} to={`/works/${r.id}`} image={r.cover_image?.url} title={capitalize(r.title)} location={formatLocation(r.location)} />
              ))}
            </Carousel>
            <div className="mt-6"><CtaLink to="/portfolio">Toutes les réalisations</CtaLink></div>
          </div>
        </section>
      )}

      <Lightbox images={viewer} index={lightbox} onClose={() => setLightbox(null)} onChange={setLightbox} />
    </article>
  );
}
